import { type ReactElement, type ReactNode } from 'react'
import { isDefined, type LockableTeamField, type TeamData, type TeamUpdateData } from '@terros/common'
import { useDisclosure } from '@mantine/hooks'
import { useForm } from '@mantine/form'
import { Group, TextInput } from '@mantine/core'
import { useToastError } from '@hooks'
import { useLockedTeamFields, usePermissionCheck, useTranslation, useUpdateTeam } from '@front/model'
import { type OptionalRowInPlaceEdit } from '../../OptionalRow'
import { ButtonCancel, ButtonSave } from '../../buttons'

export type TeamFieldInputProps = {
  label: string
  value: string | undefined
  error: ReactNode
  onChange: (value: string) => void
}

export type EditTeamFieldOptions = {
  label: string
  required?: boolean
  disabled?: boolean
  lockField?: LockableTeamField
  getValue: (team: TeamData) => string | undefined
  toUpdates: (value: string | undefined) => Partial<TeamUpdateData>
  renderInput?: (props: TeamFieldInputProps) => ReactNode
}

export const useEditTeamField = (team: TeamData, options: EditTeamFieldOptions): OptionalRowInPlaceEdit => {
  const [opened, { open, close }] = useDisclosure(false)
  const { data: canUpdate, isLoading } = usePermissionCheck({
    permissionType: 'team:update',
    targetId: team.teamId,
  })
  const { lockedFields, isLoading: isLoadingLocks } = useLockedTeamFields(team.teamId)
  const locked = isDefined(options.lockField) && lockedFields.includes(options.lockField)

  return {
    type: 'in-place',
    disabled: team.isDeleted || options.disabled || locked || isLoadingLocks || isLoading || !canUpdate,
    onEdit: team.isDeleted ? undefined : open,
    editor:
      opened && !team.isDeleted && !locked ? (
        <TeamFieldEditor key={team.teamId} options={options} team={team} onClose={close} />
      ) : undefined,
  }
}

type EditorProps = {
  team: TeamData
  options: EditTeamFieldOptions
  onClose: VoidFunction
}

const TeamFieldEditor = ({ team, options, onClose }: EditorProps): ReactElement => {
  const { t } = useTranslation()
  const form = useForm<{ value: string | undefined }>({
    initialValues: { value: options.getValue(team) },
    validate: {
      value: (fieldValue) => {
        if (!options.required || fieldValue?.trim()) return null
        return t('{{label}} is required', { label: t(options.label) })
      },
    },
  })
  const { update, isUpdating, error } = useUpdateTeam()
  useToastError(error)

  const save = async ({ value }: { value: string | undefined }): Promise<void> => {
    await update({ teamId: team.teamId, ...options.toUpdates(value?.trim()) })
    onClose()
  }

  const inputProps: TeamFieldInputProps = {
    label: t(options.label),
    value: form.getValues().value,
    error: form.errors.value,
    onChange: (nextValue) => form.setFieldValue('value', nextValue),
  }

  return (
    <form onSubmit={form.onSubmit(save)}>
      <Group gap='xs' miw={0} wrap='nowrap'>
        {options.renderInput ? (
          options.renderInput(inputProps)
        ) : (
          <TextInput aria-label={inputProps.label} autoFocus flex={1} miw={0} {...form.getInputProps('value')} />
        )}
        <ButtonCancel buttonType='icon-full' tooltip={t('Cancel')} type='button' onClick={onClose} />
        <ButtonSave buttonType='icon-full' loading={isUpdating} tooltip={t('Save')} type='submit' />
      </Group>
    </form>
  )
}
